// Ambil Paksa / Selesaikan Paksa per tahap (Owner/Admin) di Rincian Pantau Produksi Offline.
import { useEffect, useState } from "react";
import axios from "axios";
import { X, UserCog, CheckCheck, Undo2 } from "lucide-react";
import "./owner_manual.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/owner-force`;
const cfg = { withCredentials: true, timeout: 20000 };
const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Gagal memproses. Tidak ada status yang diubah."; };

export function OwnerForceActions({ orderNumber, onDone }) {
  const [pv, setPv] = useState(null);
  const [kind, setKind] = useState("");
  const load = () => axios.get(`${API}/orders/${encodeURIComponent(orderNumber)}`, cfg).then(r => setPv(r.data)).catch(() => setPv(null));
  useEffect(() => { load(); }, [orderNumber]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pv) return null;
  const close = (changed) => { setKind(""); load(); if (changed && onDone) onDone(); };
  return <div className="ofx-btns" data-testid="owner-force-actions">
    <button type="button" className="omx-trigger" onClick={() => setKind("take")} data-testid="owner-force-open-take"><UserCog size={15}/> Ambil Paksa</button>
    <button type="button" className="omx-trigger" onClick={() => setKind("finish")} data-testid="owner-force-open-finish"><CheckCheck size={15}/> Selesaikan Paksa</button>
    {(pv.requeue_jobs || []).length > 0 && <button type="button" className="omx-trigger" onClick={() => setKind("requeue")} data-testid="owner-force-open-requeue"><Undo2 size={15}/> Kembalikan ke Antrean</button>}
    {kind && <ForceDialog pv={pv} kind={kind} onClose={close}/>}
  </div>;
}

function JobPicker({ pv, pick, setPick, requeue }) {
  const items = requeue ? (pv.requeue_jobs || []).map(j => ({ ...j, key: `${j.pid}|` }))
    : pv.products.flatMap(p => p.jobs.map(j => ({ ...j, pid: p.id, pname: p.name, key: `${p.id}|${j.track}` })));
  if (!items.length) return <p className="omx-note" data-testid="owner-force-no-jobs">Tidak ada tahap produksi aktif pada nota ini.</p>;
  return <ul className="omx-prods" data-testid="owner-force-jobs">{items.map(j =>
    <li key={j.key} className="omx-prod" data-testid={`owner-force-job-${j.key}`}>
      <input type="radio" name="ofx-job" checked={pick?.key === j.key} disabled={!!j.blocked} onChange={() => setPick(j)} data-testid={`owner-force-pick-${j.key}`}/>
      <div><b>{j.pname}</b> · {j.label}
        <small>Status: {j.status} · Operator: {j.pic || "belum ada"}</small>
        {j.blocked && <small className="omx-block">{j.blocked}</small>}
      </div>
    </li>)}</ul>;
}

function ForceDialog({ pv, kind, onClose }) {
  const isTake = kind === "take";
  const isRq = kind === "requeue";
  const blockers = isRq ? (pv.requeue_blockers || []) : pv.blockers;
  const [pick, setPick] = useState(null);
  const [opId, setOpId] = useState("");
  const [reason, setReason] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ops = pick ? pv.operators.filter(o => pick.teams.includes(o.team) && o.name !== pick.pic) : [];
  const op = ops.find(o => o.id === opId);
  const valid = pick && reason.trim().length >= 5 && (!isTake || op);
  const effect = !pick ? "" : isRq
    ? `Pemegang ${pick.pic} dilepas; pekerjaan ${pick.stage} kembali ke antrean dan bisa diambil operator lain.${["Selesai", "Diambil", "Dikirim"].includes(pv.order_status) ? ` Nota dibuka lagi dari ${pv.order_status} ke Diproses.` : ""} Hasil kerja yang sudah tercatat tetap tersimpan.`
    : isTake
    ? (pick.pic ? `Pekerjaan dialihkan dari ${pick.pic} ke ${op?.name || "-"}. Hasil kerja ${pick.pic} yang sudah tercatat tetap tersimpan.` : `${op?.name || "-"} langsung ditugaskan.`)
    : (pick.track ? `Hanya jalur ${pick.track} yang ditandai selesai. Jalur lain tetap berjalan; tahap pindah otomatis bila semua jalur selesai.`
      : `Tahap ${pick.stage} diselesaikan → pindah ke ${pick.next_stage || "-"}. Produk lain dan nota tidak ikut diselesaikan.`);
  const submit = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const body = isRq ? { product_id: pick.pid, expected_pic: pick.pic, reason: reason.trim() }
        : isTake ? { product_id: pick.pid, track: pick.track, operator_id: opId, expected_pic: pick.pic, reason: reason.trim() }
        : { product_id: pick.pid, track: pick.track, expected_stage: pick.stage, reason: reason.trim() };
      await axios.post(`${API}/orders/${encodeURIComponent(pv.order_number)}/${kind}`, body, cfg);
      onClose(true);
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  const title = isRq ? "Kembalikan ke Antrean" : isTake ? "Ambil Paksa" : "Selesaikan Paksa";
  return <div className="omx-overlay" onClick={() => onClose(false)} data-testid="owner-force-dialog">
    <div className="omx-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
      <div className="omx-head"><h3>{title} · {pv.order_number}</h3>
        <button type="button" onClick={() => onClose(false)} aria-label="Tutup" data-testid="owner-force-close"><X size={18}/></button></div>
      <div className="omx-body">
        {blockers.length > 0 ? <div className="omx-err" role="alert" data-testid="owner-force-blockers">{blockers.map((b, i) => <div key={i}>{b}</div>)}</div>
        : step === 1 ? <>
          <h4>1. Pilih produk & tahap</h4>
          <JobPicker pv={pv} pick={pick} requeue={isRq} setPick={(j) => { setPick(j); setOpId(""); }}/>
          {isTake && pick && <>
            <h4>2. Operator pengganti</h4>
            <select value={opId} onChange={e => setOpId(e.target.value)} data-testid="owner-force-operator" style={{ width: "100%", minHeight: 44, fontSize: 16 }}>
              <option value="">— pilih operator tim {pick.teams.join("/")} —</option>
              {ops.map(o => <option key={o.id} value={o.id}>{o.name} · {o.team}</option>)}
            </select>
            {ops.length === 0 && <p className="omx-note">Belum ada operator aktif di tim ini.</p>}
          </>}
          <h4>{isTake ? "3" : "2"}. Alasan (wajib)</h4>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={500} data-testid="owner-force-reason"
            placeholder={isTake ? "Contoh: operator sakit, pekerjaan macet" : "Contoh: barang sudah jadi, operator lupa tekan Selesai"}/>
          <div className="omx-actions">
            <button type="button" className="soft-btn" onClick={() => onClose(false)} data-testid="owner-force-cancel">Batal</button>
            <button type="button" className="primary" disabled={!valid} onClick={() => setStep(2)} data-testid="owner-force-next">Lanjut</button>
          </div>
        </> : <div className="omx-confirm" data-testid="owner-force-confirm">
          <dl>
            <dt>Produk</dt><dd>{pick.pname} · {pick.label}</dd>
            <dt>Sebelum</dt><dd data-testid="owner-force-before">{pick.status} · Operator: {pick.pic || "belum ada"}</dd>
            <dt>Sesudah</dt><dd data-testid="owner-force-effect">{effect}</dd>
            <dt>Alasan</dt><dd>{reason.trim()}</dd>
          </dl>
          <p className="omx-note">Tidak mengubah stok, pembayaran, titik bordir, maupun hasil kerja operator. Tindakan tercatat di riwayat.</p>
          {err && <div className="omx-err" role="alert" data-testid="owner-force-error">{err}</div>}
          <div className="omx-actions">
            <button type="button" className="soft-btn" disabled={busy} onClick={() => setStep(1)} data-testid="owner-force-back">Kembali</button>
            <button type="button" className="primary" disabled={busy} onClick={submit} data-testid="owner-force-submit">{busy ? "Memproses…" : "Ya, Jalankan"}</button>
          </div>
        </div>}
        {pv.actions.length > 0 && <details className="ofx-hist" data-testid="owner-force-history"><summary>Riwayat tindakan paksa ({pv.actions.length})</summary>
          <ul className="omx-acts">{pv.actions.map(a => <li key={a.id}>
            <b>{a.label}</b> · {a.product_name} · {a.stage}{a.track ? ` (${a.track})` : ""}
            <small>{a.before.status} · {a.before.pic || "—"} → {a.after.status} · {a.after.pic || "—"}</small>
            <small>{a.by} · {new Date(a.at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} · {a.reason}</small>
          </li>)}</ul></details>}
      </div>
    </div>
  </div>;
}
